/* eslint-disable unicorn/consistent-function-scoping */

import type { Coupon, CouponIdentifiers, CouponIndex } from '../../../schema/coupon/entry.js';
import { type ICouponRepository, type IDefinitionService } from '../../domain/entry.js';
import type { ICouponZustandStoreService } from '../services/couponStoreService.js';
import { CouponZustandEntity } from './coupon/CouponZustandEntity.js';
import { createCouponModel as createCouponModelImpl } from './coupon/lib/createCouponModel.js';
import { selectCoupon } from './coupon/selectors/selectCoupon.js';
import { selectCouponBoard } from './coupon/selectors/selectCouponBoard.js';
import { selectCoupons } from './coupon/selectors/selectCoupons.js';

/**
 * Coupon repository to handle coupons.
 *
 * @param couponStoreService - Coupon service to get store instance
 * @param definitionService - Definition service to resolve definitions
 * @returns Coupon settings repository for persistence
 */
export function couponRepository(
  couponStoreService: ICouponZustandStoreService,
  definitionService: IDefinitionService
): ICouponRepository {
  const selectCouponModel = ({ couponId, couponIndex }: CouponIdentifiers): Coupon | undefined => {
    const appStore = couponStoreService.get();

    return selectCoupon(couponId, { couponIndex })(appStore.getState());
  };

  const getCoupon: ICouponRepository['getCoupon'] = async ({ couponId, couponIndex }) => {
    const appStore = couponStoreService.get();

    const model = selectCouponModel({ couponId, couponIndex });

    if (!model) {
      return undefined;
    }
    const definition = await definitionService.resolveDefinitionAsync(model.product);

    if (!definition) {
      return undefined;
    }

    return new CouponZustandEntity(model, couponIndex, definition, appStore);
  };

  /**
   * Get the internal representation of a coupon model.
   * IMPORTANT! Should only be used for internal usage.
   *
   * @param param0 Function arguments
   * @param param0.couponId Unique coupon id
   * @param param0.couponIndex Unique coupon index
   * @returns Returns Coupon model for internal usage
   */
  const getCouponModel: ICouponRepository['getCouponModel'] = ({ couponId, couponIndex }) => {
    return selectCouponModel({ couponId, couponIndex });
  };

  const getCouponCollectionModels: ICouponRepository['getCouponCollectionModels'] = ({ couponId }) => {
    const appStore = couponStoreService.get();

    return selectCoupons(couponId)(appStore.getState());
  };

  const updateCoupon: ICouponRepository['updateCoupon'] = ({ couponId, couponIndex }, fn) => {
    const appStore = couponStoreService.get();

    let isUpdated = false;
    appStore.setState(
      (state) => {
        const coupon = selectCoupon(couponId, { couponIndex })(state);
        if (!coupon) {
          return;
        }

        fn(coupon);
        isUpdated = true;
      },
      true,
      // action needed for persistence
      { type: 'updateCoupon', args: { couponId, couponIndex } }
    );

    return isUpdated;
  };

  const createCoupon: ICouponRepository['createCoupon'] = (couponId, coupon: Coupon) => {
    const appStore = couponStoreService.get();

    let couponIndex: number | undefined;

    appStore.setState((state) => {
      state.Coupons[couponId] ??= [];

      couponIndex = state.Coupons[couponId].push(coupon) - 1;
    });

    if (couponIndex === undefined) {
      return undefined;
    }

    return {
      couponId,
      couponIndex: couponIndex as CouponIndex,
    };
  };

  const deleteCoupon: ICouponRepository['deleteCoupon'] = ({ couponId, couponIndex }) => {
    const appStore = couponStoreService.get();

    let isDeleted = false;
    appStore.setState((state) => {
      let couponCollection: Coupon[] = [];
      if (state.Coupons[couponId]) {
        couponCollection = state.Coupons[couponId];
      }

      const previousLength = couponCollection.length;

      couponCollection.splice(couponIndex, 1);

      isDeleted = couponCollection.length !== previousLength;
    });

    return isDeleted;
  };

  const subscribeCoupon: ICouponRepository['subscribeCoupon'] = ({ couponId, couponIndex }, fn) => {
    const appStore = couponStoreService.get();

    return appStore.subscribe(selectCoupon(couponId, { couponIndex }), fn);
  };

  const makeCouponModel: ICouponRepository['makeCouponModel'] = (...args) => {
    return createCouponModelImpl(...args);
  };

  const getCouponBoard: ICouponRepository['getCouponBoard'] = async ({ couponId, couponIndex, boardIndex }) => {
    const couponEntity = await getCoupon({ couponId, couponIndex });

    if (!couponEntity) {
      return undefined;
    }

    const board = couponEntity.getBoard(boardIndex);

    return board;
  };

  const updateCouponBoard: ICouponRepository['updateCouponBoard'] = ({ couponId, couponIndex, boardIndex }, fn) => {
    const appStore = couponStoreService.get();

    let isUpdated = false;
    appStore.setState(
      (state) => {
        const board = selectCouponBoard(couponId, { couponIndex, boardIndex })(state);
        if (!board) {
          return;
        }

        fn(board);
        isUpdated = true;
      },
      true,
      // action needed for persistence
      { type: 'updateCouponBoard', args: { couponId, couponIndex } }
    );

    return isUpdated;
  };

  const updateCouponWallet: ICouponRepository['updateCouponWallet'] = ({ couponId, couponIndex, payload }) => {
    const appStore = couponStoreService.get();

    let isUpdated = false;
    appStore.setState(
      (state) => {
        if (state.Wallets[couponId]) {
          state.Wallets[couponId][couponIndex] = payload;
        } else {
          state.Wallets = {
            ...state.Wallets,
            [couponId]: {
              [couponIndex]: payload,
            },
          };
        }
        isUpdated = true;
      },
      true,
      // action needed for persistence
      { type: 'updateCouponWallet', args: { couponId } }
    );
    return isUpdated;
  };

  const deleteCouponWallet: ICouponRepository['deleteCouponWallet'] = ({ couponId, couponIndex }) => {
    const appStore = couponStoreService.get();

    let isDeleted = false;
    appStore.setState(
      (state) => {
        delete state.Wallets[couponId]?.[couponIndex];
        // TODO: Should remove couponId key if no more wallets exists for the coupon ?
        isDeleted = true;
      },
      true,
      // action needed for persistence
      { type: 'deleteCouponWallet', args: { couponId, couponIndex } }
    );
    return isDeleted;
  };

  const getCouponWallets: ICouponRepository['getCouponWallets'] = ({ couponId }) => {
    const appStore = couponStoreService.get();

    const wallets = appStore.getState().Wallets[couponId];

    return wallets;
  };

  return {
    getCoupon,
    getCouponModel,
    getCouponCollectionModels,
    updateCoupon,
    createCoupon,
    deleteCoupon,
    makeCouponModel,
    subscribeCoupon,
    getCouponBoard,
    updateCouponBoard,
    updateCouponWallet,
    deleteCouponWallet,
    getCouponWallets,
  } as const;
}
